import type { Error as ErrorEnvelope } from '@/lib/api/generated/model';

/**
 * The refusals the trigger-profiles endpoints can answer with, one class
 * each — same reasoning `features/secrets/errors.ts` and
 * `features/workflows/errors.ts` apply to their own error sets. The classes
 * exist rather than a bare `code` string because the constitution forbids
 * narrowing an `unknown` catch without a type guard (§2.5).
 *
 * Backend codes, per `product-x-next-service`'s
 * `app/features/triggers/errors.py`: `TRIGGER_PROFILE_NOT_FOUND` (404),
 * `TRIGGER_CONFIG_INVALID` (422), `TRIGGER_WORKFLOW_REFERENCE_INVALID`
 * (404), `MISMATCHED_CONNECTION_TYPE` (422, Spec 065 — a scheduled
 * profile's `connection_id` resolves to a connection whose `type` does not
 * match the profile's own). `CONNECTION_NOT_FOUND` (404) is the connections
 * feature's own code, propagated unchanged when a scheduled profile names a
 * `connection_id` that does not exist — mapped to a trigger-scoped class
 * here rather than importing `features/connections/errors.ts`, so this
 * feature's error handling stays self-contained (constitution Principle
 * 2.4: no cross-feature import beyond a public `index.ts`).
 */

/** Which part of the create form a refusal belongs to. */
export type TriggerErrorField = 'config' | 'form';

class TriggerError extends Error {
  readonly status: number;
  readonly field: TriggerErrorField;

  constructor(
    name: string,
    status: number,
    field: TriggerErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.status = status;
    this.field = field;
  }
}

/** `config` failed the driver's discriminated validation (FR-004). */
export class TriggerConfigInvalidError extends TriggerError {
  constructor(message?: string) {
    super(
      'TriggerConfigInvalidError',
      422,
      'config',
      message ??
        'This configuration is missing a required field or names an unknown action. Check each step.',
    );
  }
}

/**
 * `config` names a workflow, step, target workflow, or target step that does
 * not exist in this workspace (FR-002, FR-004).
 */
export class TriggerWorkflowReferenceInvalidError extends TriggerError {
  constructor(message?: string) {
    super(
      'TriggerWorkflowReferenceInvalidError',
      404,
      'config',
      message ??
        'One of the selected workflows or steps no longer exists. Re-check each selection.',
    );
  }
}

/** The trigger profile is gone — deleted or never existed in this workspace. */
export class TriggerProfileNotFoundError extends TriggerError {
  constructor(message?: string) {
    super(
      'TriggerProfileNotFoundError',
      404,
      'form',
      message ?? 'That trigger no longer exists.',
    );
  }
}

/**
 * A `{{ ... }}` template expression is malformed — unbalanced braces, an
 * empty dot-path, or a root identifier other than `signal` (spec 074, US4,
 * FR-014). Blocking, distinct from the advisory, manifest-membership
 * warning `SignalTemplateField` renders inline per field — this is the
 * server's own save-time refusal, surfaced as `TriggerProfileFormSheet`'s
 * form-level error banner.
 */
export class InvalidTemplateExpressionError extends TriggerError {
  constructor(message?: string) {
    super(
      'InvalidTemplateExpressionError',
      422,
      'config',
      message ??
        'One of the template fields has a malformed {{ }} expression. Check for unbalanced braces or a root other than "signal".',
    );
  }
}

/**
 * A scheduled (`github`/`gitlab`) profile's `connection_id` resolves to a
 * connection whose `type` does not match the profile's own — e.g. a GitHub
 * connection under a `gitlab` profile (Spec 065 FR, research.md D5's
 * client-side filtering is a UX guide, not the enforcement point).
 */
export class MismatchedConnectionTypeError extends TriggerError {
  constructor(message?: string) {
    super(
      'MismatchedConnectionTypeError',
      422,
      'config',
      message ??
        "This connection doesn't match the profile's provider. Choose a matching connection.",
    );
  }
}

/**
 * A scheduled profile's `connection_id` does not exist in this workspace —
 * e.g. it was deleted after the form's connection picker loaded.
 */
export class TriggerConnectionNotFoundError extends TriggerError {
  constructor(message?: string) {
    super(
      'TriggerConnectionNotFoundError',
      404,
      'config',
      message ?? 'That connection no longer exists. Choose another.',
    );
  }
}

/**
 * The trigger profile is soft-deleted (spec 074, FR-007) — editing,
 * re-enabling, or deleting it again is refused; delete is not reversible.
 */
export class TriggerProfileDeletedError extends TriggerError {
  constructor(message?: string) {
    super(
      'TriggerProfileDeletedError',
      422,
      'form',
      message ?? 'This trigger has been deleted and can no longer be edited.',
    );
  }
}

/**
 * A refusal this build does not recognise — a rule the service grew later.
 * Deliberately vague: claiming a cause it cannot know would be worse than
 * admitting it does not know one.
 */
export class TriggerRequestFailedError extends TriggerError {
  constructor(status: number, message?: string) {
    super(
      'TriggerRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

const BY_CODE: Record<string, (message?: string) => TriggerError> = {
  TRIGGER_CONFIG_INVALID: (m) => new TriggerConfigInvalidError(m),
  TRIGGER_WORKFLOW_REFERENCE_INVALID: (m) =>
    new TriggerWorkflowReferenceInvalidError(m),
  TRIGGER_PROFILE_NOT_FOUND: (m) => new TriggerProfileNotFoundError(m),
  INVALID_TEMPLATE_EXPRESSION: (m) => new InvalidTemplateExpressionError(m),
  MISMATCHED_CONNECTION_TYPE: (m) => new MismatchedConnectionTypeError(m),
  CONNECTION_NOT_FOUND: (m) => new TriggerConnectionNotFoundError(m),
  TRIGGER_PROFILE_DELETED: (m) => new TriggerProfileDeletedError(m),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is deliberately **not** passed through, for
 * the same reason `toSecretError` doesn't: the classes carry messages
 * written for the person looking at the form, not for a log reader. The
 * envelope's `error` code is what this reads.
 */
export function toTriggerCreateError(status: number, body: unknown): Error {
  const code =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as ErrorEnvelope).error
      : undefined;

  const construct = code ? BY_CODE[code] : undefined;
  return construct ? construct() : new TriggerRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isTriggerError(error: unknown): error is TriggerError {
  return error instanceof TriggerError;
}

/**
 * A read of a profile's signals failed (spec 090 FR-009, FR-010).
 *
 * Deliberately **not** a `TriggerError`: that hierarchy carries a
 * `TriggerErrorField` because every one of its members is a refusal of a form
 * submission, and a failed read has no field to blame. Same separation
 * `features/workspace-usage/errors.ts` draws for the same reason.
 *
 * It exists at all because FR-009 requires "this profile received no signals" and
 * "we could not find out" to be distinguishable. Resolving a failure to an empty
 * list collapses them into a confident, wrong "nothing arrived" on the one surface
 * whose entire purpose is telling a reader whether something did.
 */
export class SignalsUnavailableError extends Error {
  readonly status: number;

  constructor(status: number, message?: string) {
    super(message ?? 'The signals for this trigger could not be read.');
    this.name = 'SignalsUnavailableError';
    this.status = status;
  }
}

/** Narrows a query's `unknown` error (constitution §2.5). */
export function isSignalsUnavailable(
  error: unknown,
): error is SignalsUnavailableError {
  return error instanceof SignalsUnavailableError;
}

export type { TriggerError };
